/**
 * MarketIndicators Component
 *
 * Displays horizontal scrolling market sentiment indicators (Fear Index, AAII, etc.)
 * Extracted from SmartSummaryPage for reuse across Ideas and Pulse pages.
 */

import { FC } from 'react';
import { css } from '@emotion/react';
import { colors, spacing, fontStyles } from '../../styles';
import { MarketIndicator } from '../../apis/backend';

export interface MarketIndicatorsProps {
  indicators?: {
    aaii?: MarketIndicator;
    fearIndex?: MarketIndicator;
    fear_index?: MarketIndicator;
    volatility?: MarketIndicator;
    momentum?: MarketIndicator;
    yield_curve?: MarketIndicator;
  };
}

const getColorForSentiment = (sentiment: string) => {
  const lowerSentiment = sentiment.toLowerCase();
  if (lowerSentiment === 'positive') return colors.leaf;
  if (lowerSentiment === 'negative') return colors.berry;
  return colors.silver;
};

export const MarketIndicators: FC<MarketIndicatorsProps> = ({ indicators }) => {
  if (!indicators || Object.keys(indicators).length === 0) {
    return null;
  }

  // Support both fearIndex (camelCase) and fear_index (snake_case) from backend
  const fearIndexValue = indicators.fearIndex || indicators.fear_index;

  const indicatorData = [
    { key: 'fearIndex', label: 'Fear Index', value: fearIndexValue },
    { key: 'aaii', label: 'AAII', value: indicators.aaii },
    { key: 'volatility', label: 'Volatility', value: indicators.volatility },
    { key: 'momentum', label: 'Momentum', value: indicators.momentum },
    { key: 'yield_curve', label: 'Yield curve', value: indicators.yield_curve },
  ];

  return (
    <div
      css={css`
        margin-bottom: ${spacing.centi};
        overflow-x: auto;
        -webkit-overflow-scrolling: touch;
        scrollbar-width: none;
        -ms-overflow-style: none;

        &::-webkit-scrollbar {
          display: none;
        }
      `}
    >
      <div
        css={css`
          display: flex;
          gap: ${spacing.centi};
          min-width: max-content;
        `}
      >
        {indicatorData.map(({ key, label, value }) => {
          if (!value) return null;
          const color = getColorForSentiment(value.sentiment);

          // Render as link if URL is available, otherwise as div
          const Component = value.url ? 'a' : 'div';
          const componentProps = value.url
            ? {
                href: value.url,
                target: '_blank',
                rel: 'noopener noreferrer',
                'aria-label': `View ${label} source data`,
              }
            : {};

          return (
            <Component
              key={key}
              {...componentProps}
              css={css`
                background: ${colors.snow};
                border: 0.5px solid ${colors.cloud};
                border-radius: 8px;
                padding: ${spacing.deci};
                text-align: center;
                min-width: 100px;
                flex-shrink: 0;
                text-decoration: none;
                cursor: ${value.url ? 'pointer' : 'default'};
                transition: all 0.2s ease;

                ${value.url &&
                `
                  &:hover {
                    background: ${colors.fog};
                  }
                `}

                html[data-theme='dark'] & {
                  background: ${colors.liquorice};

                  ${value.url &&
                  `
                    &:hover {
                      background: #242323;
                    }
                  `}
                }
              `}
            >
              <div
                css={css`
                  ${fontStyles.cat}
                  color: ${colors.silver};
                  margin-bottom: ${spacing.micro};
                  font-weight: 500;
                  font-size: 12px;
                  text-transform: uppercase;
                  letter-spacing: 0.5px;

                  html[data-theme='dark'] & {
                    color: var(--text-color);
                  }
                `}
              >
                {label}
              </div>
              <div
                css={css`
                  ${fontStyles.dog}
                  color: ${color};
                  font-weight: 600;
                `}
              >
                {value.value}
              </div>
            </Component>
          );
        })}
      </div>
    </div>
  );
};

// Skeleton component for loading state
export const MarketIndicatorsSkeleton: FC = () => {
  return (
    <div
      css={css`
        margin-bottom: ${spacing.centi};
        overflow-x: auto;
        -webkit-overflow-scrolling: touch;
        scrollbar-width: none;
        -ms-overflow-style: none;

        &::-webkit-scrollbar {
          display: none;
        }
      `}
    >
      <div
        css={css`
          display: flex;
          gap: ${spacing.centi};
          min-width: max-content;
        `}
      >
        {[1, 2, 3, 4, 5].map((i) => (
          <div
            key={i}
            css={css`
              background: ${colors.snow};
              border: 0.5px solid ${colors.cloud};
              border-radius: 8px;
              padding: ${spacing.deci};
              text-align: center;
              min-width: 100px;
              flex-shrink: 0;

              html[data-theme='dark'] & {
                background: ${colors.liquorice};
              }
            `}
          >
            <div
              css={css`
                width: 60px;
                height: 12px;
                background: ${colors.cloud};
                border-radius: 4px;
                margin: 0 auto ${spacing.micro};
                animation: pulse 1.5s ease-in-out infinite;

                @keyframes pulse {
                  0%,
                  100% {
                    opacity: 1;
                  }
                  50% {
                    opacity: 0.5;
                  }
                }

                html[data-theme='dark'] & {
                  background: ${colors.tungsten};
                }
              `}
            />
            <div
              css={css`
                width: 40px;
                height: 16px;
                background: ${colors.cloud};
                border-radius: 4px;
                margin: 0 auto;
                animation: pulse 1.5s ease-in-out infinite;

                html[data-theme='dark'] & {
                  background: ${colors.tungsten};
                }
              `}
            />
          </div>
        ))}
      </div>
    </div>
  );
};

export default MarketIndicators;
